import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authorizationApi } from "../../../api/authorization";
import { unwrapResponse } from "../../../api/client";
import { useAuth } from "../../../auth/AuthContext";

interface BusinessProfile {
  id: string;
  name: string;
  plan?: string;
  status?: boolean | string;
  email?: string;
  raw: any;
}

const STORAGE_KEY = "biznifyBusinessProfile";
const PROFILE_ID_KEY = "profileId";
const CLIENT_PROFILE_KEY = "clientBusinessProfile";
const USER_ID_KEY = "userId";

export default function SelectProfile() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const returnUrl = searchParams.get("returnUrl") || "/pos-checkin";

  useEffect(() => {
    const email =
      (user?.profile as any)?.email ||
      (user?.profile as any)?.Email ||
      (user?.profile as any)?.upn ||
      "";

    if (!email) {
      setError("No email found in the authenticated user profile.");
      setLoading(false);
      return;
    }

    authorizationApi
      .getBusinessProfilesByEmail(email)
      .then((response) => {
        const data = unwrapResponse<any[]>(response);
        if (Array.isArray(data)) {
          const mapped = data.map((item) => ({
            id: String(
              item?.businessProfile?.id ??
                item?.businessProfileId ??
                item?.businessProfileID ??
                item?.BusinessProfileId ??
                item?.id ??
                ""
            ),
            name:
              item?.businessProfile?.name ??
              item?.businessName ??
              item?.name ??
              item?.Name ??
              "Business",
            plan:
              item?.subscriptionPlan?.name ??
              item?.packageName ??
              item?.planName ??
              item?.PlanName,
            status:
              item?.subscriptionStatus ??
              item?.status ??
              item?.Status,
            email: item?.userMail ?? item?.email ?? item?.Email,
            raw: item,
          }));
          setProfiles(mapped);
        } else {
          setProfiles([]);
        }
      })
      .catch(() => {
        setError("Failed to load business profiles.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user]);

  const handleSelect = (profile: BusinessProfile) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    localStorage.setItem(CLIENT_PROFILE_KEY, JSON.stringify(profile.raw));
    if (profile.id) {
      localStorage.setItem(PROFILE_ID_KEY, profile.id);
    }
    const rawUserId = profile.raw?.userId ?? profile.raw?.UserId;
    if (rawUserId != null) {
      localStorage.setItem(USER_ID_KEY, String(rawUserId));
    }
    navigate(returnUrl, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden lg:flex w-1/3 items-center justify-center bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="text-center space-y-4">
          <div className="text-3xl font-bold text-gray-900">Biznify POS</div>
          <p className="text-sm text-gray-600 max-w-xs">
            Please choose your business profile to continue.
          </p>
        </div>
      </div>

      <div className="flex-1 p-6 lg:p-10">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">
            Choose a Business Profile
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            We found multiple profiles for your account.
          </p>

          {loading && (
            <div className="mt-8 text-gray-500">Loading profiles...</div>
          )}
          {error && (
            <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-4 py-3">
              {error}
            </div>
          )}

          {!loading && !error && profiles.length === 0 && (
            <div className="mt-8 text-sm text-gray-500">
              No profiles found for this account.
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => handleSelect(profile)}
                className="bg-white border border-gray-200 rounded-xl p-4 text-left hover:border-teal-400 hover:shadow-sm transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {profile.name}
                    </div>
                    {profile.email && (
                      <div className="text-xs text-gray-500">{profile.email}</div>
                    )}
                  </div>
                  <span className="px-3 py-1 text-xs rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                    Check In
                  </span>
                </div>
                {(profile.plan || profile.status !== undefined) && (
                  <div className="mt-3 text-xs text-gray-600 flex gap-3 flex-wrap">
                    {profile.plan && (
                      <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {profile.plan}
                      </span>
                    )}
                    {profile.status !== undefined && (
                      <span
                        className={`px-2 py-1 rounded border ${
                          profile.status === true || profile.status === "Active"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                      >
                        {profile.status === true || profile.status === "Active"
                          ? "Active"
                          : "Inactive"}
                      </span>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
